import type { FieldDefinition, FieldPlacement, FieldType, FormSchema, FormSection } from '@/features/contract/types'

// Pełna lista typów pól jest single-source-of-truth w `contract/types.ts`;
// admin/forms re-exportuje aby konsumenci pod tą ścieżką mieli FieldType
// pod jednym importem (wzorzec historyczny).
export type { FieldDefinition, FieldPlacement, FieldType, FormSchema, FormSection }

/** Display label per field type for the admin "Typ" dropdown. */
export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  TEXT: 'Tekst (krótki)',
  TEXTAREA: 'Tekst (wieloliniowy)',
  RICH_TEXT: 'Tekst formatowany',
  NUMBER: 'Liczba',
  CURRENCY: 'Kwota (zł)',
  DATE: 'Data',
  BOOLEAN: 'Tak / Nie',
  SELECT: 'Lista jednokrotna',
  MULTI_SELECT: 'Lista wielokrotna',
  CHECKBOX_GROUP: 'Grupa checkboxów',
  REFERENCE: 'Referencja',
  EMAIL: 'E-mail',
  PHONE: 'Telefon',
  URL: 'URL',
  IBAN: 'IBAN',
  NIP: 'NIP',
  PESEL: 'PESEL',
  SEPARATOR: 'Separator (linia)',
  SPACER: 'Pusta przestrzeń',
  INSTRUCTION: 'Instrukcja (info box)',
  HEADER: 'Nagłówek sekcji',
  FILE: 'Plik / załącznik',
  GALLERY: 'Galeria zdjęć',
}

/** Field types without value - render only as visual layout helpers. */
export const PRESENTATIONAL_TYPES: ReadonlyArray<FieldType> = ['SEPARATOR', 'SPACER', 'INSTRUCTION', 'HEADER']

/** Field types that need {@link FieldDefinition.options} populated. */
export const OPTIONS_TYPES: ReadonlyArray<FieldType> = ['SELECT', 'MULTI_SELECT', 'CHECKBOX_GROUP']

export type FieldOption = { value: string; labelPl: string }

export type ValidationRules = {
  min?: number | null
  max?: number | null
  regex?: string | null
  maxLength?: number | null
}

export type SectionRequest = {
  code: string
  labelPl: string
  sortOrder: number
  isActive: boolean
  renderTarget: 'TAB' | 'SIDEBAR'
}

export type SectionUpdateRequest = {
  labelPl: string
  sortOrder: number
  isActive: boolean
  renderTarget: 'TAB' | 'SIDEBAR'
}

export type FieldRequest = {
  fieldCode: string
  /** Kody sekcji, do których pole jest przypisane (N:M - jedno pole moze byc w wielu sekcjach). */
  sectionCodes: string[]
  labelPl: string
  fieldType: FieldType
  isRequired: boolean
  isFilterable: boolean
  validation?: ValidationRules | null
  options?: FieldOption[] | null
  referenceTable?: string | null
  helpText?: string | null
  documentTypeId?: number | null
  semanticRole?: SemanticRole | null
  /** F-VER: tryby weryfikacji pola (CLICK/DOCUMENT/COMMENT). Puste = pole nie podlega. */
  verificationModes?: string[] | null
  /** F-VER: kody ról uprawnionych do zatwierdzenia (mirror chk_field_definition_verify_consistency). */
  verifierRoleCodes?: string[] | null
  /** F-3a: mapowanie source-code -> dot-path w payload (mirror chk po backend). */
  externalKeys?: Record<string, string> | null
  isActive: boolean
}

// Zsynchronizowane 1:1 z backendowym enumem SemanticRole (platform.forms.domain.model).
export type SemanticRole =
  | 'CONTRACT_TOTAL_AMOUNT'
  | 'CONTRACT_TOTAL_AMOUNT_OVERRIDE'
  | 'CONTRACT_CURRENCY'
  | 'CONTRACT_SIGNED_DATE'
  | 'CONTRACT_COMPLETED_DATE'
  | 'ELEMENT_PRICE'
  | 'ELEMENT_DELIVERY_DATE'
  | 'ELEMENT_INSTALL_DATE'

export const SEMANTIC_ROLE_LABELS: Record<SemanticRole, string> = {
  CONTRACT_TOTAL_AMOUNT: 'Kwota umowy (składnik)',
  CONTRACT_TOTAL_AMOUNT_OVERRIDE: 'Kwota umowy (nadpisanie)',
  CONTRACT_CURRENCY: 'Waluta umowy',
  CONTRACT_SIGNED_DATE: 'Data podpisania',
  CONTRACT_COMPLETED_DATE: 'Data zakończenia',
  ELEMENT_PRICE: 'Cena produktu',
  ELEMENT_DELIVERY_DATE: 'Data dostawy produktu',
  ELEMENT_INSTALL_DATE: 'Data montażu produktu',
}

/** Dozwolone typy pola dla każdej roli. UI ukrywa select gdy fieldType niezgodny. */
export const SEMANTIC_ROLE_ALLOWED_TYPES: Record<SemanticRole, FieldType[]> = {
  CONTRACT_TOTAL_AMOUNT: ['NUMBER', 'CURRENCY'],
  CONTRACT_TOTAL_AMOUNT_OVERRIDE: ['NUMBER', 'CURRENCY'],
  CONTRACT_CURRENCY: ['TEXT', 'SELECT'],
  CONTRACT_SIGNED_DATE: ['DATE'],
  CONTRACT_COMPLETED_DATE: ['DATE'],
  ELEMENT_PRICE: ['NUMBER', 'CURRENCY'],
  ELEMENT_DELIVERY_DATE: ['DATE'],
  ELEMENT_INSTALL_DATE: ['DATE'],
}

/** object_type, do którego należy dana rola (role umowy vs per-produktowe). */
export const SEMANTIC_ROLE_OBJECT_TYPE: Record<SemanticRole, string> = {
  CONTRACT_TOTAL_AMOUNT: 'contract',
  CONTRACT_TOTAL_AMOUNT_OVERRIDE: 'contract',
  CONTRACT_CURRENCY: 'contract',
  CONTRACT_SIGNED_DATE: 'contract',
  CONTRACT_COMPLETED_DATE: 'contract',
  ELEMENT_PRICE: 'product',
  ELEMENT_DELIVERY_DATE: 'product',
  ELEMENT_INSTALL_DATE: 'product',
}

/** Role przypisywalne do pola danego object_type i typu pola (część wspólna obu ograniczeń). */
export function compatibleSemanticRoles(objectType: string, fieldType: FieldType): SemanticRole[] {
  return (Object.keys(SEMANTIC_ROLE_ALLOWED_TYPES) as SemanticRole[]).filter(
    r => SEMANTIC_ROLE_OBJECT_TYPE[r] === objectType && SEMANTIC_ROLE_ALLOWED_TYPES[r].includes(fieldType),
  )
}

export type FieldUpdateRequest = Omit<FieldRequest, 'fieldCode'>

export type AccessPolicy = {
  id: number
  objectType: string
  fieldCode: string | null
  sectionCode: string | null
  roleCode: string
  workflowState: string | null
  canView: boolean
  canEdit: boolean
  effect: 'GRANT' | 'DENY'
  priority: number
}

export type AccessPolicyRequest = Omit<AccessPolicy, 'id' | 'objectType'>
